import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Shakes are excluded from stock tracking per the boutique's rule (made on
// the spot). Anything else the admin might create (extras, seasonal packs)
// is also treated as untracked unless they explicitly assign a stock_qty.
const TRACKED_CATEGORIES = new Set(["cups", "tubs", "packs", "boxes"]);
const LOW_THRESHOLD = 5;

function stockBadgeClass(qty) {
  if (qty === null || qty === undefined) return "border-line text-espresso-light";
  if (qty <= 0) return "border-rose-500 text-rose-600 bg-rose-50";
  if (qty <= LOW_THRESHOLD) return "border-gold text-gold-hover bg-ivory-100";
  return "border-emerald-600 text-emerald-700 bg-emerald-50";
}

/**
 * InventoryPanel — Admin → Inventory tab. Lists every cup / tub / pack / box,
 * shows current stock, and lets the admin restock inline (e.g., after the
 * morning bake). Setting stock to 0 auto-sells-out the product; setting it
 * back to a positive number auto-re-enables it. Shakes are excluded up-front.
 */
export default function InventoryPanel() {
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({}); // { [productId]: "12" } while typing
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      const tracked = data.filter((p) => TRACKED_CATEGORIES.has(p.category));
      tracked.sort((a, b) => (a.category > b.category ? 1 : -1) || a.name.localeCompare(b.name));
      setProducts(tracked);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load inventory.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStock = async (p, rawQty) => {
    const qty = rawQty === "" || rawQty === null ? null : Math.max(0, parseInt(rawQty, 10) || 0);
    setSavingId(p.id);
    try {
      await api.put(`/admin/products/${p.id}/stock`, { stock_qty: qty });
      toast.success(qty === null ? `${p.name} — untracked` : `${p.name} → ${qty}`);
      setDrafts((d) => { const c = { ...d }; delete c[p.id]; return c; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const bumpBy = (p, delta) => {
    const base = drafts[p.id] !== undefined ? parseInt(drafts[p.id], 10) : (p.stock_qty ?? 0);
    setDrafts((d) => ({ ...d, [p.id]: String(Math.max(0, (base || 0) + delta)) }));
  };

  const tracked = products.filter((p) => p.stock_qty !== null && p.stock_qty !== undefined);
  const untracked = products.filter((p) => p.stock_qty === null || p.stock_qty === undefined);
  const soldOut = tracked.filter((p) => (p.stock_qty ?? 0) <= 0).length;
  const low = tracked.filter((p) => (p.stock_qty ?? 0) > 0 && (p.stock_qty ?? 0) <= LOW_THRESHOLD).length;
  const totalUnits = tracked.reduce((s, p) => s + (p.stock_qty ?? 0), 0);

  return (
    <div className="mt-8" data-testid="inventory-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-xs text-espresso-light inline-flex items-center gap-2">
            <Package size={14} strokeWidth={1.5} /> Inventory
          </div>
          <h2 className="mt-2 font-serif text-3xl text-espresso">Today's stock, live.</h2>
          <p className="text-espresso-light text-sm mt-1">
            Shakes are made on the spot so they aren't tracked here.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="inventory-refresh"
          className="label-xs inline-flex items-center gap-2 px-3 py-2 border border-line rounded-full text-espresso-light hover:text-espresso hover:border-espresso transition-colors"
        >
          <RefreshCw size={14} strokeWidth={1.75} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="border border-line p-4 text-center" data-testid="inv-total">
          <div className="label-xs text-espresso-light">Total units</div>
          <div className="font-serif text-3xl text-espresso mt-1">{totalUnits}</div>
        </div>
        <div className="border border-line p-4 text-center" data-testid="inv-low">
          <div className="label-xs text-espresso-light">Low stock (≤{LOW_THRESHOLD})</div>
          <div className={`font-serif text-3xl mt-1 ${low ? "text-gold-hover" : "text-espresso"}`}>{low}</div>
        </div>
        <div className="border border-line p-4 text-center" data-testid="inv-sold-out">
          <div className="label-xs text-espresso-light">Sold out</div>
          <div className={`font-serif text-3xl mt-1 ${soldOut ? "text-rose-600" : "text-espresso"}`}>{soldOut}</div>
        </div>
      </div>

      <div className="space-y-3">
        {products.length === 0 && (
          <div className="text-sm text-espresso-light label-xs">
            {loading ? "Loading…" : "No cup / tub / pack / box products yet."}
          </div>
        )}
        {products.map((p) => {
          const draft = drafts[p.id];
          const currentQty = p.stock_qty ?? null;
          const isUntracked = currentQty === null;
          const shownValue = draft !== undefined ? draft : (isUntracked ? "" : String(currentQty));
          return (
            <div
              key={p.id}
              data-testid={`inv-row-${p.slug}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center border border-line bg-ivory p-4"
            >
              <div className="md:col-span-4">
                <div className="font-serif text-lg text-espresso">{p.name}</div>
                <div className="label-xs text-espresso-light">{p.category} · ₹{p.price}</div>
              </div>
              <div className="md:col-span-2">
                <span
                  className={`label-xs inline-flex px-2 py-0.5 rounded-full border ${stockBadgeClass(currentQty)}`}
                  data-testid={`inv-badge-${p.slug}`}
                >
                  {isUntracked ? "Untracked" : currentQty <= 0 ? "Sold out" : `${currentQty} in stock`}
                </span>
              </div>
              <div className="md:col-span-4 flex items-center gap-2">
                <button
                  onClick={() => bumpBy(p, -1)}
                  data-testid={`inv-minus-${p.slug}`}
                  className="w-8 h-8 border border-line hover:border-espresso rounded-full text-espresso"
                  disabled={savingId === p.id}
                >−</button>
                <Input
                  type="number"
                  min={0}
                  value={shownValue}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                  placeholder={isUntracked ? "untracked" : "0"}
                  className="w-24 text-center"
                  data-testid={`inv-input-${p.slug}`}
                />
                <button
                  onClick={() => bumpBy(p, +1)}
                  data-testid={`inv-plus-${p.slug}`}
                  className="w-8 h-8 border border-line hover:border-espresso rounded-full text-espresso"
                  disabled={savingId === p.id}
                >+</button>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button
                  size="sm"
                  onClick={() => setStock(p, shownValue)}
                  disabled={savingId === p.id || draft === undefined}
                  data-testid={`inv-save-${p.slug}`}
                >
                  {savingId === p.id ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {untracked.length > 0 && (
        <p className="mt-8 label-xs text-espresso-light">
          {untracked.length} product{untracked.length === 1 ? "" : "s"} are currently untracked — save any number above to opt in, or clear the input + Save to opt back out.
        </p>
      )}
    </div>
  );
}
